import { useEffect, useState } from "react";
import CheckCircleIcon from "@heroicons/react/24/solid/CheckCircleIcon";
import XMarkIcon from "@heroicons/react/24/solid/XMarkIcon";
import UserIcon from "@heroicons/react/24/solid/UserIcon";
import PhoneIcon from "@heroicons/react/24/solid/PhoneIcon";
import BuildingOffice2Icon from "@heroicons/react/24/solid/BuildingOffice2Icon";
import ArrowRightIcon from "@heroicons/react/24/solid/ArrowRightIcon";
import BaseModal from "../BaseModal";
import BaseButtonSecondary from "../BaseButtonSecondary";
import BaseButtonPrimary from "../BaseButtonPrimary";
import * as strings from "../../strings";
import StyledMessage from "../StyledMessage";
import StyledSwitchGroup from "../StyledSwitchGroup";
import BaseInputText from "../BaseInputText";
import { DbWrite } from "../../database";
import { z } from "zod";
import { ExistingSiteKey } from "../../models/site-key";
import { useSiteKeyDocStore } from "../../store/site-key-doc";
import { phoneUtils } from "../../utils";
import { logger } from "../../logging";

const twilioSettingsSchema = z
  .object({
    useDoubleCallForwarding: z.boolean().optional(),
    accountSid: z.string().optional(),
    authToken: z.string().optional(),
    firstDoubleFordwardNumber: z.string().optional(),
    secondDoubleFordwardNumber: z.string().optional(),
  })
  .optional();

const phoneSettingsSchema = z
  .object({
    twilio: twilioSettingsSchema,
    callForwardingEnabled: z.boolean().optional(),
    callForwardingNumber: z.string().optional(),
    mainNumber: z.string().optional(),
    siteSipDomain: z.string().optional(),
  })
  .optional();

export interface TwilioSettings {
  useDoubleCallForwarding?: boolean;
  accountSid?: string;
  authToken?: string;
  firstDoubleFordwardNumber?: string;
  secondDoubleFordwardNumber?: string;
}

export interface PhoneSettings {
  twilio?: TwilioSettings;
  callForwardingEnabled?: boolean;
  callForwardingNumber?: string;
  mainNumber?: string;
  siteSipDomain?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  // siteKeyDoc: ExistingSiteKey;
}

export default function VoipSettingsDialog({ open, onClose }: Props) {
  const siteKeyDoc = useSiteKeyDocStore((state) => state.siteKeyDoc);

  const [settings, setSettings] = useState<PhoneSettings>({
    twilio: {
      useDoubleCallForwarding: undefined,
      accountSid: undefined,
      authToken: undefined,
      firstDoubleFordwardNumber: undefined,
      secondDoubleFordwardNumber: undefined,
    },
    callForwardingEnabled: undefined,
    callForwardingNumber: undefined,
    mainNumber: undefined,
    siteSipDomain: undefined,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSettingUpTwilio, setIsSettingUpTwilio] = useState(false);
  const [isSettingUpForwarding, setIsSettingUpForwarding] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [secondNumberError, setSecondNumberError] = useState<string | null>(
    null,
  );

  useEffect(() => {
    const phoneSettings = siteKeyDoc?.customizations?.phones;
    // Parse the phone settings using zod
    const parsedSettings = phoneSettingsSchema.parse(phoneSettings);
    if (parsedSettings) {
      setSettings(parsedSettings);
    }
  }, [siteKeyDoc]);

  const handleSetupTwilio = async () => {
    if (!siteKeyDoc) {
      return;
    }
    setIsSettingUpTwilio(true);
    try {
      await DbWrite.twilio.createSubAccount(siteKeyDoc.id);
    } catch (error) {
      logger.error(error);
      setErrorMessage("Failed to set up call integration");
    } finally {
      setIsSettingUpTwilio(false);
    }
  };

  const handleSetupForwarding = async () => {
    if (!siteKeyDoc) {
      return;
    }
    setIsSettingUpForwarding(true);
    try {
      await DbWrite.twilio.setUpFirstCallForwardingNumber(siteKeyDoc.id);
    } catch (error) {
      logger.error(error);
      setErrorMessage("Failed to set up first call forwarding number");
    } finally {
      setIsSettingUpForwarding(false);
    }
  };

  const validatePhoneNumber = (phoneNumber: string): boolean => {
    try {
      const { warning } = phoneUtils.parse(phoneNumber);
      if (warning) {
        setSecondNumberError("Please enter a valid 10-digit phone number");
        return false;
      }
      setSecondNumberError(null);
      return true;
    } catch (error) {
      setSecondNumberError("Please enter a valid phone number");
      return false;
    }
  };

  const handleToggleDoubleForwarding = async (checked: boolean) => {
    if (!siteKeyDoc) return;

    setIsSubmitting(true);
    try {
      await DbWrite.siteKey.editCustomizations(siteKeyDoc.id, {
        customizations: {
          phones: {
            ...settings,
            twilio: {
              ...settings.twilio!,
              useDoubleCallForwarding: checked,
            },
          },
        },
      });
      setSettings((prev) => ({
        ...prev,
        twilio: {
          ...prev.twilio!,
          useDoubleCallForwarding: checked,
        },
      }));
    } catch (error) {
      logger.error(error);
      setErrorMessage("Failed to save settings");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSave = async () => {
    if (!siteKeyDoc) {
      return;
    }

    // Clear any previous errors
    setErrorMessage(null);
    setSecondNumberError(null);

    // Validate phone number if double forwarding is enabled
    if (
      settings.twilio?.useDoubleCallForwarding &&
      settings.twilio?.secondDoubleFordwardNumber
    ) {
      if (!validatePhoneNumber(settings.twilio.secondDoubleFordwardNumber)) {
        return;
      }
    }

    setIsSubmitting(true);
    try {
      await DbWrite.siteKey.editCustomizations(siteKeyDoc.id, {
        customizations: {
          phones: settings,
        },
      });
      onClose();
    } catch (error) {
      logger.error(error);
      setErrorMessage("Failed to save phone settings");
    } finally {
      setIsSubmitting(false);
    }
  };

  const header = (
    <div className="mb-4 flex w-full items-center justify-between rounded-t-lg bg-primary p-8 text-left text-white">
      <h1 className="inline-flex items-center text-xl font-semibold">
        Call Integration Settings
      </h1>
      <button type="button" onClick={onClose}>
        <XMarkIcon
          aria-label="close voip settings dialog"
          className="h-6 text-white"
        />
      </button>
    </div>
  );

  return (
    <BaseModal
      closeModal={() => {}} // prevent unintentionally closing dialog
      open={open}
      title={header}
      allowOverflowY={true}
      parentDivStyles="inline-block transform overflow-hidden max-w-7xl rounded-lg bg-white text-left align-middle shadow-xl transition-all"
    >
      <form
        autoComplete="off"
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
        className="px-4 pb-4 md:px-8 md:pb-6"
      >
        <div className="mb-4 space-y-2">
          {!settings.twilio?.accountSid && (
            <div className="space-y-4">
              <div className="rounded-lg bg-gray-50 p-4">
                <h3 className="mb-3 text-lg font-semibold text-gray-900">
                  Benefits of Stilt Call Integration
                </h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <div className="font-medium text-gray-900">
                      Call Tracking & Analytics
                    </div>
                    <p className="text-sm text-gray-600">
                      Track every call's source, duration, and outcome. Get
                      detailed metrics and KPIs to measure campaign
                      effectiveness and team performance.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <div className="font-medium text-gray-900">
                      Smart Caller ID
                    </div>
                    <p className="text-sm text-gray-600">
                      Instantly identify callers and see their complete history
                      before answering. Provide personalized service from the
                      first ring.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <div className="font-medium text-gray-900">
                      Call Recording
                    </div>
                    <p className="text-sm text-gray-600">
                      Automatically record calls for quality assurance,
                      training, and documentation. Review conversations to
                      improve customer service.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <div className="font-medium text-gray-900">
                      Marketing Attribution
                    </div>
                    <p className="text-sm text-gray-600">
                      Track which marketing campaigns and channels drive calls.
                      Measure ROI and optimize your advertising spend with real
                      data.
                    </p>
                  </div>
                </div>
              </div>
              <p>
                After setting up your initial call integration, you will be able
                to choose between double call forwarding or porting your numbers
                over to Stilt. You can test out Stilt's call tracking using
                double call forwarding before porting numbers over to Stilt.
              </p>
            </div>
          )}
          {!settings.twilio?.accountSid ? (
            <BaseButtonPrimary
              type="button"
              onClick={handleSetupTwilio}
              isBusy={isSettingUpTwilio}
              busyText="Setting up..."
            >
              Set Up Call Integration
            </BaseButtonPrimary>
          ) : (
            <>
              <div className="mb-4 rounded-lg bg-blue-50 p-4 text-blue-800">
                <h3 className="mb-1 font-medium">
                  Choose Your Integration Method
                </h3>
                <p className="text-sm">
                  Select one of the following options below to integrate your
                  phone system with Stilt. We recommend starting with Double
                  Call Forwarding to test the system before porting your
                  numbers.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div className="w-full rounded-md border border-gray-500 px-3 py-2">
                  <h2 className="py-2 text-xl font-medium">
                    Double Call Forwarding
                  </h2>
                  <div className="flex flex-col space-y-4">
                    <p>
                      When you enable Double Call Forwarding, your calls are
                      forwarded to a Stilt number and then forwarded back to a
                      second number that you own. This method is recommended if
                      you want to test out Stilt's call tracking before porting
                      numbers over to Stilt. With this method, outbound calls
                      will not be tracked unless you click the Call button on
                      the Customer's page or configure your phone system to dial
                      out using a linked number. You can still purchase and use
                      tracking numbers for ad campaigns with double call
                      forwarding.
                    </p>

                    <div className="flex items-center justify-center gap-2 py-4">
                      <div className="flex flex-col items-center">
                        <UserIcon className="h-8 w-8 text-gray-600" />
                        <span className="text-xs">Customer</span>
                      </div>
                      <ArrowRightIcon className="h-6 w-6 text-gray-400" />
                      <div className="flex flex-col items-center">
                        <PhoneIcon className="h-8 w-8 text-gray-600" />
                        <span className="text-xs">Your Number</span>
                      </div>
                      <ArrowRightIcon className="h-6 w-6 text-gray-400" />
                      <div className="flex flex-col items-center">
                        <img
                          src="/black_yellow_hardhat_transparent_bg.svg"
                          alt="Stilt"
                          className="h-8 w-8"
                        />
                        <span className="text-xs">First Forwarding</span>
                      </div>
                      <ArrowRightIcon className="h-6 w-6 text-gray-400" />
                      <div className="flex flex-col items-center">
                        <BuildingOffice2Icon className="h-8 w-8 text-gray-600" />
                        <span className="text-xs">Your Office</span>
                      </div>
                    </div>

                    <StyledSwitchGroup
                      onBlur={() => {}}
                      readableName="Use Double Call Forwarding"
                      checked={
                        settings.twilio?.useDoubleCallForwarding ?? false
                      }
                      onChange={(checked: unknown) => {
                        if (typeof checked === "boolean") {
                          handleToggleDoubleForwarding(checked);
                        }
                      }}
                      id="useDoubleCallForwarding"
                      name="useDoubleCallForwarding"
                    />
                    {settings.twilio?.useDoubleCallForwarding && (
                      <div className="space-y-6">
                        {!settings.twilio?.firstDoubleFordwardNumber ? (
                          <BaseButtonPrimary
                            type="button"
                            onClick={handleSetupForwarding}
                            isBusy={isSettingUpForwarding}
                            busyText="Setting up..."
                          >
                            Set Up First Forwarding Number
                          </BaseButtonPrimary>
                        ) : (
                          <div className="flex flex-col space-y-2 rounded-md border border-gray-500 px-3 py-2">
                            <div className="flex flex-row items-center gap-2">
                              <p>
                                First Forwarding Number:{" "}
                                {phoneUtils.display(
                                  settings.twilio?.firstDoubleFordwardNumber,
                                )}
                              </p>
                              <CheckCircleIcon className="h-6 w-6 text-green-500" />
                            </div>
                            {/* Note to tell them to set their phones to forward to the first forwarding number */}
                            <div className="rounded-md bg-orange-200 px-2 py-1 text-sm text-black">
                              Please set your phones to forward to this number.
                            </div>
                          </div>
                        )}

                        <div className="space-y-2">
                          <BaseInputText
                            inputName="secondDoubleFordwardNumber"
                            text="Forward Calls Back To"
                            value={settings.twilio?.secondDoubleFordwardNumber}
                            onChange={(e) => {
                              setSettings((prev) => ({
                                ...prev,
                                twilio: {
                                  ...prev.twilio!,
                                  secondDoubleFordwardNumber: e.target.value,
                                },
                              }));
                            }}
                            admin
                          />
                          {secondNumberError && (
                            <p className="text-sm text-red-500">
                              {secondNumberError}
                            </p>
                          )}
                          <div className="rounded-md bg-orange-200 px-2 py-1 text-sm text-black">
                            This should be a second number that you own and do
                            not advertise to customers. Calls will come in on
                            this line to your office. If you do not have a
                            second line, please contact support for assistance.
                          </div>
                          <BaseButtonPrimary
                            type="submit"
                            formNoValidate
                            isBusy={isSubmitting}
                            busyText={strings.buttons.BUSY_SAVING}
                            className="mt-4 w-full justify-center uppercase"
                          >
                            {strings.buttons.SAVE}
                          </BaseButtonPrimary>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="w-full space-y-4 rounded-md border border-gray-500 px-3 py-2">
                  <h2 className="py-2 text-xl font-medium">
                    Port Your Numbers
                  </h2>
                  <p>
                    For a full integration, you can port your numbers over to
                    Stilt. Please contact support to get started.
                  </p>
                  <BaseButtonPrimary
                    type="button"
                    onClick={() => {
                      window.location.href =
                        "mailto:support@stiltwork.com?subject=Number%20Porting%20Request";
                    }}
                  >
                    Contact Support
                  </BaseButtonPrimary>
                </div>
              </div>
            </>
          )}
        </div>

        {/* <div className="space-y-6"> */}
        {/* Basic Settings */}
        {/* <div className="space-y-4">
                        <BaseInputText
                            inputName="mainNumber"
                            text="Main Number"
                            value={settings.mainNumber}
                            onChange={(e) =>
                                setSettings(prev => ({ ...prev, mainNumber: e.target.value }))
                            }
                            admin
                        />

                        <div className="max-w-fit rounded-md border border-gray-500 px-3 py-2">
                            <StyledSwitchGroup
                                onBlur={() => { }}
                                readableName="Call Forwarding Enabled"
                                checked={settings.callForwardingEnabled ?? false}
                                onChange={(checked: unknown) => {
                                    if (typeof checked === 'boolean') {
                                        setSettings(prev => ({ ...prev, callForwardingEnabled: checked }))
                                    }
                                }}
                                id="callForwardingEnabled"
                                name="callForwardingEnabled"
                            />
                        </div>

                        <BaseInputText
                            inputName="callForwardingNumber"
                            text="Call Forwarding Number"
                            value={settings.callForwardingNumber}
                            onChange={(e) =>
                                setSettings(prev => ({ ...prev, callForwardingNumber: e.target.value }))
                            }
                            admin
                        />
                    </div> */}
        {/* </div> */}

        {errorMessage && (
          <div className="mt-4">
            <StyledMessage type="error">
              {{ message: errorMessage }}
            </StyledMessage>
          </div>
        )}
      </form>
    </BaseModal>
  );
}
